import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, query, updateDoc, where } from "firebase/firestore";
import { CuratedOrganization, PartnerSchool } from "models";
import { db } from "services";
import { dbCollections } from "utilities";

/**
 * Retrieves a list of all partner-schools from (not peoplegrove), using the map view type.
 * @returns A Promise containing the list of all partner schools.
 */
export async function getAllPartnerSchools(): Promise<PartnerSchool[]> {
  const partnerSchoolQuery = query(
    collection(db, dbCollections.memberInfo),
    where("type", "==", "partner-school")
  );
  const snipshot = await getDocs(partnerSchoolQuery);
  return snipshot.docs.map(doc => {
    return { ...doc.data(), id: doc.id } as PartnerSchool;
  });
}

/**
 * Retrieves more detailed data for one specific partner school on (where?).
 * @param username The username of a specific partner school on (not peoplegrove).
 * @returns A more detailed blob of data for a specific partner school on (not PeopleGrove).
 */
export async function getPartnerSchool(id: string): Promise<PartnerSchool | undefined> {
  const docRef = doc(db, dbCollections.memberInfo, id);
  const snapshot = await getDoc(docRef);
  return snapshot.exists() ? ({ ...snapshot.data(), id: snapshot.id } as PartnerSchool) : undefined;
}

/**
 * Retrieves all curated alumni from the database.
 * @returns A Promise containing the retrieved documents as CuratedAlum objects.
 */
export async function getPartnerSchoolInfo(): Promise<CuratedOrganization[]> {
  const partnerSchoolQuery = query(
    collection(db, dbCollections.memberInfo),
    where("type", "==", "partner-school")
  );
  const snapshot = await getDocs(partnerSchoolQuery);
  return snapshot.docs.map(doc => {
    return { ...doc.data(), id: doc.id } as CuratedOrganization;
  });
}

export async function getSpecificPartnerSchool(
  id: string,
): Promise<CuratedOrganization | undefined> {
  const docRef = doc(db, dbCollections.memberInfo, id);
  const snapshot = await getDoc(docRef);
  return snapshot.exists()
    ? ({ ...snapshot.data(), id: snapshot.id } as CuratedOrganization)
    : undefined;
}

/**
 * Retrieves a specific may term from the database as a CuratedAlum.
 * @param id The ID of the may term to retrieve.
 * @returns A Promise containing a specific may term, or undefined if the ID doesn't exist.
 */
export async function getPartnerSchoolStory(id: string): Promise<CuratedOrganization | undefined> {
  const docRef = doc(db, dbCollections.memberInfo, id);
  const snapshot = await getDoc(docRef);
  return snapshot.exists()
    ? ({ ...snapshot.data(), id: snapshot.id } as CuratedOrganization)
    : undefined;
}

/**
 * Updates an existing may term in the database. Some or all fields may
 * be updated, depending on what's included in the newAlum param.
 * @param id The ID of the may term to update.
 * @param newMayTerm The updated version of the may term.
 * @returns A Promise representing the status of the update.
 */
export async function updatePartnerSchoolStory(
  id: string,
  newMayTerm: CuratedOrganization,
): Promise<void> {
  const docRef = doc(db, dbCollections.memberInfo, id);
  return updateDoc(docRef, { ...newMayTerm });
}

/**
 * Adds a new may term to the database and returns the id of the created document.
 * @param newMayTerm The may term to add.
 * @returns A Promise containing the ID of the added may term.
 */
export async function addPartnerSchoolStory(newMayTerm: CuratedOrganization): Promise<string> {
  const collectionRef = collection(db, dbCollections.memberInfo);
  const snapshot = await addDoc(collectionRef, newMayTerm);
  return snapshot.id;
}

/**
 * Deletes an item from the database.
 * @param id The ID of the item to delete.
 * @returns A Promise representing the status of the delete.
 */
export async function deleteSchoolStory(id: string): Promise<void> {
  const docRef = doc(db, dbCollections.memberInfo, id);
  return deleteDoc(docRef);
}
